import '../operations.css';
import './delivery.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Trash2, Send, RefreshCw, Mail, ArrowRight, Inbox } from 'lucide-react';
import { Card, StatCard } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { campaignsApi } from '../../api/campaigns';
import { deliverabilityApi } from '../../api/deliverability';
import type { SeedMailbox, SeedTest } from '../../api/deliverability';
import { SMTPFormModal } from '../SendingProfiles/SMTPFormModal';
import { PostfixServerSetup } from './PostfixServerSetup';

const inputClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

const emptySeedForm = {
  label: '',
  email: '',
  imap_host: '',
  imap_port: 993,
  imap_username: '',
  imap_password: '',
  ignore_cert: false,
};

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function folderBadge(folder: SeedTest['folder'], status: SeedTest['status']) {
  if (folder === 'inbox') {
    return <span className="badge bg-green-100 text-green-800">Inbox</span>;
  }
  if (folder === 'spam') {
    return <span className="badge bg-red-100 text-red-800">Spam</span>;
  }
  if (status === 'check_failed') {
    return <span className="badge bg-yellow-100 text-yellow-800">Check failed</span>;
  }
  if (status === 'send_failed') {
    return <span className="badge bg-red-100 text-red-700">Send failed</span>;
  }
  if (status === 'not_found') {
    return <span className="badge bg-gray-100 text-gray-600">Not found</span>;
  }
  return <span className="badge bg-gray-100 text-gray-600">Pending</span>;
}

export function Deliverability() {
  const queryClient = useQueryClient();
  const [seedModal, setSeedModal] = useState(false);
  const [seedForm, setSeedForm] = useState(emptySeedForm);
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const [postfixModal, setPostfixModal] = useState(false);

  const seedsQuery = useQuery({
    queryKey: ['seed-mailboxes'],
    queryFn: async () => (await deliverabilityApi.listSeeds()).data,
  });

  const campaignsQuery = useQuery({
    queryKey: ['campaign-summaries'],
    queryFn: async () => (await campaignsApi.getSummary()).data.campaigns,
  });

  const seedTestsQuery = useQuery({
    queryKey: ['seed-tests', campaignId],
    queryFn: async () => (await deliverabilityApi.listSeedTests(Number(campaignId))).data,
    enabled: campaignId !== '',
  });

  const deliveryQuery = useQuery({
    queryKey: ['campaign-delivery', campaignId],
    queryFn: async () => (await deliverabilityApi.delivery(Number(campaignId))).data,
    enabled: campaignId !== '',
  });

  const createSeedMutation = useMutation({
    mutationFn: () => deliverabilityApi.createSeed(seedForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seed-mailboxes'] });
      setSeedModal(false);
      setSeedForm(emptySeedForm);
    },
  });

  const deleteSeedMutation = useMutation({
    mutationFn: (id: number) => deliverabilityApi.deleteSeed(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seed-mailboxes'] }),
  });

  const runSeedTestMutation = useMutation({
    mutationFn: () => deliverabilityApi.runSeedTest(Number(campaignId)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['seed-tests', campaignId] }),
  });

  const checkMutation = useMutation({
    mutationFn: (seedTestId: number) => deliverabilityApi.checkPlacement(seedTestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seed-tests', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-delivery', campaignId] });
    },
  });

  const seeds = seedsQuery.data ?? [];
  const seedById = new Map<number, SeedMailbox>(seeds.map((s) => [s.id, s]));
  const delivery = deliveryQuery.data;

  const seedColumns = [
    { key: 'label', header: 'Label', render: (s: SeedMailbox) => s.label },
    { key: 'email', header: 'Email', render: (s: SeedMailbox) => s.email },
    {
      key: 'imap_host',
      header: 'IMAP server',
      render: (s: SeedMailbox) => `${s.imap_host}:${s.imap_port}`,
    },
    {
      key: 'actions',
      header: '',
      render: (s: SeedMailbox) => (
        <button
          type="button"
          aria-label={`Delete ${s.label}`}
          className="text-gray-400 hover:text-red-600"
          onClick={() => deleteSeedMutation.mutate(s.id)}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const seedTestColumns = [
    {
      key: 'seed',
      header: 'Seed mailbox',
      render: (t: SeedTest) => seedById.get(t.seed_mailbox_id)?.email ?? `#${t.seed_mailbox_id}`,
    },
    {
      key: 'sent_at',
      header: 'Sent',
      render: (t: SeedTest) => formatTime(t.sent_at),
    },
    {
      key: 'folder',
      header: 'Placement',
      render: (t: SeedTest) => (
        <div className="flex flex-col gap-1">
          {folderBadge(t.folder, t.status)}
          {t.error && <span className="text-xs text-red-600">{t.error}</span>}
        </div>
      ),
    },
    {
      key: 'checked_at',
      header: 'Checked',
      render: (t: SeedTest) => formatTime(t.checked_at),
    },
    {
      key: 'actions',
      header: '',
      render: (t: SeedTest) => (
        <Button
          size="sm"
          variant="secondary"
          loading={checkMutation.isPending && checkMutation.variables === t.id}
          disabled={t.status === 'send_failed'}
          onClick={() => checkMutation.mutate(t.id)}
        >
          Check placement
        </Button>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="operations-eyebrow">Deliverability</p>
          <h1 className="text-2xl font-bold text-gray-900">Delivery workspace</h1>
          <p className="text-gray-500 mt-1">
            Configure outbound delivery, then check where your messages arrive.
          </p>
        </div>
        <Button onClick={() => setSeedModal(true)}>
          <Plus className="w-4 h-4 mr-1" /> Add seed mailbox
        </Button>
      </div>

      <section className="delivery-routes" aria-label="Sending and inbox placement">
        <div className="delivery-outbound">
          <div className="delivery-path" aria-hidden="true"><Mail size={20} /><span>AwareNow</span><ArrowRight size={18} /><span>SMTP / Postfix</span><ArrowRight size={18} /><Inbox size={20} /></div>
          <h2>Outbound delivery with SMTP or Postfix</h2>
          <p>Sending profiles connect your campaign to an SMTP relay. Use an existing mail provider or configure your Postfix connection with its hostname, port, From address, and authentication.</p>
          <div className="delivery-route-actions"><Button onClick={() => setPostfixModal(true)}>Configure Postfix connection</Button><a href="/sending-profiles">Manage sending profiles</a></div>
          <p className="delivery-route-note">Authenticated submission uses port 587. Private relay connections use port 25 with restricted relay access.</p>
        </div>
        <div className="delivery-inbound">
          <Inbox size={24} aria-hidden="true" />
          <h2>Inbox placement with IMAP</h2>
          <p>Seed mailboxes receive test messages. IMAP reads those mailboxes to check whether a message reached the inbox or spam folder.</p>
          <p>Configure outbound sending first, then add a seed mailbox below to measure placement.</p>
        </div>
      </section>

      <PostfixServerSetup />

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Seed mailboxes for IMAP placement checks</h3>
        {seedsQuery.isError ? (
          <p role="alert" className="text-red-700">Could not load seed mailboxes.</p>
        ) : (
          <Table
            columns={seedColumns}
            data={seeds}
            keyExtractor={(s) => s.id}
            emptyMessage="No seed mailboxes yet. Add one to run inbox-placement checks."
          />
        )}
      </Card>

      <Card>
        <div className="operations-section-title">
          <h3 className="text-lg font-semibold text-gray-900">Seed test</h3>
        </div>
        <div className="flex items-end gap-3 flex-wrap mb-4">
          <div className="min-w-64">
            <label htmlFor="deliverability-campaign" className={labelClass}>Campaign</label>
            <select
              id="deliverability-campaign"
              className={inputClass}
              value={campaignId}
              onChange={(e) =>
                setCampaignId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <option value="">Select a campaign</option>
              {(campaignsQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <Button
            disabled={campaignId === '' || seeds.length === 0}
            loading={runSeedTestMutation.isPending}
            onClick={() => runSeedTestMutation.mutate()}
          >
            <Send className="w-4 h-4 mr-1" /> Run seed test
          </Button>
        </div>
        {campaignId !== '' && seeds.length === 0 && (
          <p className="text-sm text-gray-500 mb-2">Add a seed mailbox before running a seed test.</p>
        )}
        {campaignId !== '' &&
          (seedTestsQuery.isError ? (
            <p role="alert" className="text-red-700">Could not load seed tests.</p>
          ) : (
            <Table
              columns={seedTestColumns}
              data={seedTestsQuery.data ?? []}
              keyExtractor={(t) => t.id}
              emptyMessage="No seed tests for this campaign yet."
            />
          ))}
      </Card>

      <Card>
        <div className="operations-section-title">
          <h3 className="text-lg font-semibold text-gray-900">Delivery metrics</h3>
          {delivery?.label === 'unwhitelisted' && (
            <span className="badge bg-yellow-100 text-yellow-800">Unwhitelisted campaign</span>
          )}
        </div>
        {campaignId === '' ? (
          <p className="text-sm text-gray-500">Select a campaign to view delivery metrics.</p>
        ) : deliveryQuery.isError ? (
          <p role="alert" className="text-red-700">Could not load delivery metrics.</p>
        ) : delivery ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Sent" value={delivery.sent} color="indigo" />
              <StatCard title="Delivered" value={delivery.delivered} color="green" />
              <StatCard title="Bounced" value={delivery.bounced} color="red" />
              <StatCard title="Bounce rate" value={`${delivery.bounce_rate.toFixed(1)}%`} color="yellow" />
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Opened {delivery.opened} · Clicked {delivery.clicked} · Submitted {delivery.submitted} ·
              Reported {delivery.reported}
              {delivery.inbox_estimated && (
                <span className="ml-2 badge bg-green-100 text-green-800">
                  <RefreshCw className="w-3 h-3 mr-1" /> Inbox placement verified by seed
                </span>
              )}
            </p>
          </>
        ) : null}
      </Card>

      <Modal isOpen={seedModal} onClose={() => setSeedModal(false)} title="Add seed mailbox">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            createSeedMutation.mutate();
          }}
        >
          <div>
            <label htmlFor="seed-label" className={labelClass}>Label</label>
            <input
              id="seed-label"
              required
              className={inputClass}
              value={seedForm.label}
              onChange={(e) => setSeedForm({ ...seedForm, label: e.target.value })}
              placeholder="Gmail seed"
            />
          </div>
          <div>
            <label htmlFor="seed-email" className={labelClass}>Seed email address</label>
            <input
              id="seed-email"
              required
              type="email"
              className={inputClass}
              value={seedForm.email}
              onChange={(e) => setSeedForm({ ...seedForm, email: e.target.value })}
              placeholder="seed@gmail.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="seed-imap-host" className={labelClass}>IMAP host</label>
              <input
                id="seed-imap-host"
                required
                className={inputClass}
                value={seedForm.imap_host}
                onChange={(e) => setSeedForm({ ...seedForm, imap_host: e.target.value })}
                placeholder="imap.gmail.com"
              />
            </div>
            <div>
              <label htmlFor="seed-imap-port" className={labelClass}>IMAP port</label>
              <input
                id="seed-imap-port"
                required
                type="number"
                min={1}
                max={65535}
                className={inputClass}
                value={seedForm.imap_port}
                onChange={(e) =>
                  setSeedForm({ ...seedForm, imap_port: Number(e.target.value) })
                }
              />
            </div>
          </div>
          <div>
            <label htmlFor="seed-imap-username" className={labelClass}>IMAP username</label>
            <input
              id="seed-imap-username"
              required
              className={inputClass}
              value={seedForm.imap_username}
              onChange={(e) => setSeedForm({ ...seedForm, imap_username: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="seed-imap-password" className={labelClass}>IMAP password</label>
            <input
              id="seed-imap-password"
              required
              type="password"
              className={inputClass}
              value={seedForm.imap_password}
              onChange={(e) => setSeedForm({ ...seedForm, imap_password: e.target.value })}
            />
          </div>
          <label htmlFor="seed-ignore-cert" className="flex items-center gap-2 text-sm text-gray-700">
            <input
              id="seed-ignore-cert"
              type="checkbox"
              checked={seedForm.ignore_cert}
              onChange={(e) => setSeedForm({ ...seedForm, ignore_cert: e.target.checked })}
            />
            Ignore certificate errors
          </label>
          {createSeedMutation.isError && (
            <p role="alert" className="text-red-700 text-sm">Could not save the seed mailbox.</p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setSeedModal(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createSeedMutation.isPending}>
              Save seed mailbox
            </Button>
          </div>
        </form>
      </Modal>
      <SMTPFormModal isOpen={postfixModal} onClose={() => setPostfixModal(false)} smtp={null} initialPreset="postfix_submission" />
    </div>
  );
}
